<script setup lang="ts">
definePageMeta({ layout: false, colorMode: 'light' })

const route = useRoute()
const tenantSlug = String(route.params.tenantSlug)
const api = usePortalAccountApi(tenantSlug)
const { setSession } = usePortalAuth(tenantSlug)

const email = ref('')
const password = ref('')
const loading = ref(false)
const googleLoading = ref(false)
const error = ref<string | null>(null)

const justVerified = computed(() => route.query.verified === '1')
const verifyError = computed(() => route.query.verify_error === '1')
const googleFailed = computed(() => route.query.error === 'google_failed')

async function submit() {
  error.value = null
  loading.value = true
  try {
    const res = await api.login(email.value, password.value)
    setSession(res.token, res.account)
    await navigateTo(`/client/${tenantSlug}`)
  } catch (e: unknown) {
    const err = e as { data?: { message?: string, errors?: Record<string, string[]> } }
    error.value = err.data?.errors?.email?.[0] ?? err.data?.message ?? 'Could not sign in. Check your email and password.'
  } finally {
    loading.value = false
  }
}

async function signInWithGoogle() {
  googleLoading.value = true
  try {
    const res = await api.googleRedirectUrl()
    window.location.href = res.url
  } catch {
    error.value = 'Could not start Google sign-in. Please try again.'
    googleLoading.value = false
  }
}
</script>

<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center p-6">
    <div class="w-full max-w-sm space-y-6">
      <h1 class="text-xl font-semibold text-center">
        Client portal sign in
      </h1>

      <p
        v-if="justVerified"
        class="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-3"
      >
        Your email is verified — sign in below.
      </p>
      <p
        v-if="verifyError"
        class="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3"
      >
        That verification link is invalid or expired.
      </p>
      <p
        v-if="googleFailed"
        class="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3"
      >
        Google sign-in failed. Please try again.
      </p>

      <button
        type="button"
        :disabled="googleLoading"
        class="w-full flex items-center justify-center gap-2 border rounded py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
        @click="signInWithGoogle"
      >
        <img
          src="/images/icons/google-g.svg"
          alt=""
          class="size-4"
        >
        {{ googleLoading ? 'Redirecting…' : 'Continue with Google' }}
      </button>

      <div class="flex items-center gap-3 text-xs text-gray-400">
        <div class="flex-1 border-t" />
        or
        <div class="flex-1 border-t" />
      </div>

      <form
        class="space-y-4"
        @submit.prevent="submit"
      >
        <div>
          <label class="block text-sm text-gray-700 mb-1">Email</label>
          <input
            v-model="email"
            type="email"
            required
            class="w-full border rounded px-3 py-2 text-sm"
          >
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Password</label>
          <input
            v-model="password"
            type="password"
            required
            class="w-full border rounded px-3 py-2 text-sm"
          >
        </div>
        <p
          v-if="error"
          class="text-sm text-red-600"
        >
          {{ error }}
        </p>
        <button
          type="submit"
          :disabled="loading"
          class="w-full bg-black text-white rounded py-2 text-sm disabled:opacity-50"
        >
          {{ loading ? 'Signing in…' : 'Sign in' }}
        </button>
      </form>

      <div class="flex items-center justify-between text-xs text-gray-500">
        <NuxtLink
          :to="`/client/${tenantSlug}/forgot-password`"
          class="hover:underline"
        >
          Forgot password?
        </NuxtLink>
        <NuxtLink
          :to="`/client/${tenantSlug}/register`"
          class="hover:underline"
        >
          Create an account
        </NuxtLink>
      </div>
    </div>
  </div>
</template>
